// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankAccount } from '@client/banks/types/account';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import { hasLinkableAccountIban } from '@client/banks/utils/account-reference';
import produce from 'immer';

const clearMatchingIban = <T extends { iban: string }>(
  items: T[] | undefined,
  iban: string,
): T[] | undefined => {
  if (!items?.length) {
    return items;
  }

  return items.map(item => (item.iban === iban ? { ...item, iban: '' } : item));
};

/** Removes a liquidity account and clears `iban` on instruments that linked to it. */
export const deleteAccountAndUnlinkInstruments = (
  form: BankUploadForm,
  accountIndex: number,
): BankUploadForm => {
  const account = form.accounts[accountIndex];

  if (!account) {
    return form;
  }

  const linkedIban = hasLinkableAccountIban(account.iban)
    ? account.iban.trim()
    : null;

  return produce(form, draft => {
    draft.accounts.splice(accountIndex, 1);

    if (!linkedIban) {
      return;
    }

    draft.positions = clearMatchingIban(draft.positions, linkedIban) ?? [];
    draft.debts = clearMatchingIban(draft.debts, linkedIban) ?? [];
    draft.transactions = clearMatchingIban(draft.transactions, linkedIban) ?? [];
  });
};

export type AppendLinkedAccountResult = {
  accounts: BankAccount[];
  instrumentIban: string;
};

export const appendLinkedAccount = (
  accounts: BankAccount[],
  account: BankAccount,
): AppendLinkedAccountResult => ({
  accounts: [account, ...accounts],
  instrumentIban: account.iban.trim(),
});
